import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Link2, Plus, Trash2, Save, Eye, EyeOff, GripVertical,
  ArrowUp, ArrowDown, ExternalLink, Copy, Loader2, Globe, X,
  Music, Users, Calendar, Sparkles
} from 'lucide-react';
import './MyLinks.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = 'https://anyonemusicrecords.com';

const CATEGORY_OPTIONS = [
  { value: 'music', label: 'Music', icon: Music },
  { value: 'social', label: 'Social', icon: Users },
  { value: 'shows', label: 'Shows', icon: Calendar },
  { value: 'custom', label: 'Custom', icon: Globe },
];

const MyLinks = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [manualLinks, setManualLinks] = useState([]);
  const [hiddenAutoLinks, setHiddenAutoLinks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLink, setNewLink] = useState({ title: '', url: '', category: 'custom' });

  const getToken = () => localStorage.getItem('artistToken');
  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => { fetchLinkPage(); }, []);

  const fetchLinkPage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/link-page`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPageData(data);
        setManualLinks(data.manual_links || []);
        setHiddenAutoLinks(data.hidden_auto_links || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load link page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/artist/link-page`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          manual_links: manualLinks,
          hidden_auto_links: hiddenAutoLinks
        })
      });
      if (res.ok) {
        toast.success('Link page saved!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoLink = (key) => {
    setHiddenAutoLinks(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const addManualLink = () => {
    if (!newLink.title.trim() || !newLink.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    const link = {
      id: crypto.randomUUID(),
      title: newLink.title.trim(),
      url: newLink.url.trim(),
      category: newLink.category,
      order: manualLinks.length,
      visible: true
    };
    setManualLinks(prev => [...prev, link]);
    setNewLink({ title: '', url: '', category: 'custom' });
    setShowAddForm(false);
  };

  const removeManualLink = (id) => {
    setManualLinks(prev => prev.filter(l => l.id !== id));
  };

  const moveLink = (index, direction) => {
    const newLinks = [...manualLinks];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newLinks.length) return;
    [newLinks[index], newLinks[swapIdx]] = [newLinks[swapIdx], newLinks[index]];
    newLinks.forEach((l, i) => l.order = i);
    setManualLinks(newLinks);
  };

  const toggleManualVisibility = (id) => {
    setManualLinks(prev => prev.map(l =>
      l.id === id ? { ...l, visible: !l.visible } : l
    ));
  };

  const copyLinkPageUrl = () => {
    if (pageData?.slug) {
      navigator.clipboard.writeText(`${BASE_URL}/links/${pageData.slug}`);
      toast.success('Link page URL copied!');
    } else {
      toast.error('No public profile linked yet');
    }
  };

  if (loading) {
    return (
      <div className="my-links-page">
        <div className="loading-state">
          <Loader2 className="spinner" size={32} />
          <p>Loading your link page...</p>
        </div>
      </div>
    );
  }

  const autoLinks = pageData?.auto_links || [];

  return (
    <div className="my-links-page" data-testid="my-links-page">
      <header className="links-header">
        <div className="header-content">
          <h1>My Links</h1>
          <p>Manage your Linktree-style page to share on social media</p>
        </div>
        <div className="header-actions">
          {pageData?.slug && (
            <button className="btn btn-outline" onClick={copyLinkPageUrl} data-testid="copy-page-url">
              <Copy size={16} />
              Copy URL
            </button>
          )}
          {pageData?.slug && (
            <a
              href={`/links/${pageData.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              data-testid="preview-link"
            >
              <ExternalLink size={16} />
              Preview
            </a>
          )}
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-links-btn"
          >
            {saving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </header>

      {pageData?.slug && (
        <div className="page-url-bar" data-testid="page-url-display">
          <Link2 size={16} />
          <span>{BASE_URL}/links/{pageData.slug}</span>
        </div>
      )}

      {!pageData?.slug && (
        <div className="notice-bar">
          <Sparkles size={16} />
          <span>Your link page will be available once your account is linked to a public artist profile.</span>
        </div>
      )}

      {/* Auto-Pulled Links */}
      {autoLinks.length > 0 && (
        <section className="links-section" data-testid="auto-links-section">
          <h2 className="section-title">
            <Globe size={18} />
            Auto-Pulled Links
            <span className="section-hint">From your profile. Toggle visibility below.</span>
          </h2>
          <div className="auto-links-grid">
            {autoLinks.map((link) => {
              const isHidden = hiddenAutoLinks.includes(link.key);
              return (
                <div
                  key={link.key}
                  className={`auto-link-item ${isHidden ? 'hidden-link' : ''}`}
                  data-testid={`auto-link-${link.key}`}
                >
                  <div className="auto-link-info">
                    <span className="auto-link-platform">{link.title}</span>
                    <span className="auto-link-url">{link.url?.substring(0, 40)}...</span>
                  </div>
                  <button
                    className={`toggle-btn ${isHidden ? 'off' : 'on'}`}
                    onClick={() => toggleAutoLink(link.key)}
                    title={isHidden ? 'Show on link page' : 'Hide from link page'}
                    data-testid={`toggle-${link.key}`}
                  >
                    {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                    {isHidden ? 'Hidden' : 'Visible'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Manual Links */}
      <section className="links-section" data-testid="manual-links-section">
        <div className="section-header-row">
          <h2 className="section-title">
            <Plus size={18} />
            Custom Links
          </h2>
          <button
            className="btn btn-small"
            onClick={() => setShowAddForm(true)}
            data-testid="add-link-btn"
          >
            <Plus size={14} />
            Add Link
          </button>
        </div>

        {showAddForm && (
          <div className="add-link-form" data-testid="add-link-form">
            <div className="form-row">
              <input
                type="text"
                placeholder="Link title (e.g., Buy Tickets)"
                value={newLink.title}
                onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                data-testid="new-link-title"
              />
              <input
                type="url"
                placeholder="URL (https://...)"
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                data-testid="new-link-url"
              />
            </div>
            <div className="form-row">
              <select
                value={newLink.category}
                onChange={(e) => setNewLink({ ...newLink, category: e.target.value })}
                data-testid="new-link-category"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="form-buttons">
                <button className="btn btn-small btn-primary" onClick={addManualLink} data-testid="confirm-add-link">
                  Add
                </button>
                <button className="btn btn-small btn-ghost" onClick={() => setShowAddForm(false)}>
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        )}

        {manualLinks.length === 0 ? (
          <div className="empty-manual">
            <p>No custom links yet. Add links to shows, merch, music, or anything else.</p>
          </div>
        ) : (
          <div className="manual-links-list">
            {manualLinks.map((link, index) => (
              <div key={link.id} className={`manual-link-item ${!link.visible ? 'hidden-link' : ''}`} data-testid={`manual-link-${link.id}`}>
                <div className="link-order-controls">
                  <button
                    className="order-btn"
                    onClick={() => moveLink(index, -1)}
                    disabled={index === 0}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    className="order-btn"
                    onClick={() => moveLink(index, 1)}
                    disabled={index === manualLinks.length - 1}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
                <div className="manual-link-info">
                  <span className="manual-link-title">{link.title}</span>
                  <span className="manual-link-meta">
                    <span className={`category-tag ${link.category}`}>{link.category}</span>
                    {link.url?.substring(0, 35)}...
                  </span>
                </div>
                <div className="manual-link-actions">
                  <button
                    className={`toggle-btn small ${link.visible ? 'on' : 'off'}`}
                    onClick={() => toggleManualVisibility(link.id)}
                    title={link.visible ? 'Hide' : 'Show'}
                  >
                    {link.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => removeManualLink(link.id)}
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MyLinks;
